import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react"

const STORAGE_KEY = "diobox_badge_ui_settings"

type BadgeUISettings = {
  displayUnit: "mm" | "in"
  showGrid: boolean
  snapping: boolean
}

const loadSettings = (): BadgeUISettings | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored) as BadgeUISettings
    if (parsed.displayUnit !== "mm" && parsed.displayUnit !== "in") return null
    if (typeof parsed.showGrid !== "boolean") return null

    // Saved before snapping could be turned off
    return { ...parsed, snapping: typeof parsed.snapping === "boolean" ? parsed.snapping : true }
  } catch {
    return null
  }
}

const saveSettings = (settings: BadgeUISettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

/**
 * The Badge Builder's display preferences: the unit, the grid and snapping.
 * Zoom, pan and the panels belong to the canvas editor.
 */
type BadgeUIState = {
  // Display unit
  displayUnit: "mm" | "in"
  setDisplayUnit: (unit: "mm" | "in") => void
  // Grid
  showGrid: boolean
  setShowGrid: (show: boolean) => void
  /** Elements snap to the badge, the other elements and the grid while moved or resized. */
  snapping: boolean
  setSnapping: (snapping: boolean) => void
}

export const BadgeUIContext = createContext<BadgeUIState | null>(null)

export const useBadgeUI = () => {
  const context = useContext(BadgeUIContext)

  if (!context) {
    throw new Error(
      "useBadgeUI must be used within a BadgeUIProvider",
    )
  }

  return context
}

type BadgeUIProviderProps = {
  children: React.ReactNode
  /**
   * Initial display unit (default: "mm")
   */
  initialDisplayUnit?: "mm" | "in"
}

/**
 * BadgeUIProvider
 *
 * Manages the Badge Builder's display preferences (display unit, grid, snapping), persisted to
 * localStorage.
 *
 * @example
 * ```tsx
 * <BadgeUIProvider>
 *   <BadgeBuilderProvider {...props}>
 *     <BadgeBuilderContent />
 *   </BadgeBuilderProvider>
 * </BadgeUIProvider>
 * ```
 */
export const BadgeUIProvider = ({
  children,
  initialDisplayUnit = "mm",
}: BadgeUIProviderProps) => {
  const [savedSettings] = useState(() => loadSettings())
  const [displayUnit, setDisplayUnitState] = useState<"mm" | "in">(
    savedSettings?.displayUnit ?? initialDisplayUnit,
  )
  const [showGrid, setShowGridState] = useState(savedSettings?.showGrid ?? false)
  const [snapping, setSnapping] = useState(savedSettings?.snapping ?? true)

  useEffect(() => {
    saveSettings({ displayUnit, showGrid, snapping })
  }, [displayUnit, showGrid, snapping])

  const setDisplayUnit = useCallback((unit: "mm" | "in") => {
    setDisplayUnitState(unit)
  }, [])

  const setShowGrid = useCallback((show: boolean) => {
    setShowGridState(show)
  }, [])

  const state = useMemo<BadgeUIState>(() => ({
    displayUnit,
    setDisplayUnit,
    showGrid,
    setShowGrid,
    snapping,
    setSnapping,
  }), [
    displayUnit, setDisplayUnit,
    showGrid, setShowGrid,
    snapping,
  ])

  return (
    <BadgeUIContext.Provider value={state}>
      {children}
    </BadgeUIContext.Provider>
  )
}
